/**
 * Tijd in een vaste tijdzone (standaard Europe/Amsterdam), los van de
 * tijdzone van de browser.
 *
 * 03-10-2026 (Jordy): op Mass Messages → Create kies je datum en tijd in
 * Amsterdamse tijd, ook als je laptop in een andere tijdzone staat. De server
 * krijgt het moment in UTC, zodat er niets te raden valt.
 *
 * 07-10-2026 (Jordy): "zodat chatters in hun eigen tijdzone kunnen plannen".
 * Dezelfde rekensom werkt nu voor elke IANA-zone (bv. Asia/Manila); de zone
 * komt uit de voorkeur van de gebruiker (lib/tijdzone.ts). Alleen de invoer en
 * de weergave hangen van de zone af: naar de server gaat altijd hetzelfde
 * absolute moment in UTC. `amsterdamNaarUtc` en `alsAmsterdam` blijven bestaan
 * en geven precies hetzelfde als vroeger.
 */
export const AMSTERDAM = 'Europe/Amsterdam'

const DELEN = new Map<string, Intl.DateTimeFormat>()

function delenIn(zone: string): Intl.DateTimeFormat {
  let f = DELEN.get(zone)
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone: zone, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
    DELEN.set(zone, f)
  }
  return f
}

/** Een zone die de browser kent, anders Amsterdam (zoals vóór 07-10-2026). */
export function geldigeZone(zone: unknown): string {
  if (typeof zone !== 'string' || !zone.trim()) return AMSTERDAM
  try {
    delenIn(zone.trim())
    return zone.trim()
  } catch {
    return AMSTERDAM
  }
}

/** Korte naam voor op het scherm: "Amsterdam", of de IANA-naam ("Asia/Manila"). */
export function zoneLabel(zone: string): string {
  return zone === AMSTERDAM ? 'Amsterdam' : zone
}

function delenRecord(ms: number, zone: string): Record<string, string> {
  const d: Record<string, string> = {}
  for (const p of delenIn(zone).formatToParts(new Date(ms))) {
    if (p.type !== 'literal') d[p.type] = p.value
  }
  return d
}

/** Hoeveel minuten de zone op dit moment vóór loopt op UTC (Amsterdam: 60 of 120). */
function voorsprongMinuten(ms: number, zone: string): number {
  const r = delenRecord(ms, zone)
  const d = Object.fromEntries(Object.entries(r).map(([k, v]) => [k, Number(v)]))
  const alsUtc = Date.UTC(d.year, d.month - 1, d.day, d.hour, d.minute, d.second)
  return Math.round((alsUtc - Math.floor(ms / 1000) * 1000) / 60_000)
}

/** "2026-10-20" + "22:50" in deze zone → ISO-tijd in UTC; null bij een fout. */
export function zoneNaarUtc(datum: string, tijd: string, zone: string = AMSTERDAM): string | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datum)
  const t = /^(\d{2}):(\d{2})$/.exec(tijd)
  if (!d || !t) return null
  const gok = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]))
  if (!Number.isFinite(gok)) return null
  // Twee stappen: rond de klokwissel kan de voorsprong op het echte moment
  // anders zijn dan op het geraden moment.
  let ms = gok - voorsprongMinuten(gok, zone) * 60_000
  ms = gok - voorsprongMinuten(ms, zone) * 60_000
  return new Date(ms).toISOString()
}

/** "2026-10-05" + "20:30" in Amsterdam → ISO-tijd in UTC; null bij een fout. */
export function amsterdamNaarUtc(datum: string, tijd: string): string | null {
  return zoneNaarUtc(datum, tijd, AMSTERDAM)
}

/** Een moment zoals deze zone het ziet: kalenderdag en tijd ("20:30"). */
export function zoneDelen(iso: string | null | undefined, zone: string = AMSTERDAM): { datum: string; tijd: string } | null {
  if (!iso) return null
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return null
  const d = delenRecord(ms, zone)
  return { datum: `${d.year}-${d.month}-${d.day}`, tijd: `${d.hour}:${d.minute}` }
}

/**
 * Datum en tijd uit zone `van` omzetten naar zone `naar`, met hetzelfde
 * absolute moment (bij het wisselen van de zone verschuift er niets).
 * Ongeldige invoer blijft zoals hij is.
 */
export function zetOm(m: { datum: string; tijd: string }, van: string, naar: string): { datum: string; tijd: string } {
  if (van === naar) return m
  return zoneDelen(zoneNaarUtc(m.datum, m.tijd, van), naar) ?? m
}

const KORT = new Map<string, Intl.DateTimeFormat>()

/**
 * Een moment zoals deze zone het ziet, voor op het scherm: "Thu 22-10-2026 21:30".
 * 16-10-2026 (Jordy): in Mass Messages overal Nederlands datumformaat
 * DD-MM-YYYY, 24-uurs; de weekdag blijft Engels zoals de rest van het dashboard.
 */
export function alsZone(iso: string | null | undefined, zone: string = AMSTERDAM): string {
  if (!iso) return '—'
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return '—'
  let f = KORT.get(zone)
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone: zone, hourCycle: 'h23', weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
    KORT.set(zone, f)
  }
  const d: Record<string, string> = {}
  for (const p of f.formatToParts(new Date(ms))) d[p.type] = p.value
  return `${d.weekday} ${d.day}-${d.month}-${d.year} ${d.hour}:${d.minute}`
}

/** "2026-10-22" -> "22-10-2026" (een kalenderdag, zonder tijdzone). */
export function nlDatum(datum: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(datum ?? '')
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (datum ?? '—')
}

const WEEKDAG_KORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** "2026-10-22" -> "Thu 22-10-2026". */
export function nlDagLabel(datum: string): string {
  const d = new Date(`${datum}T12:00:00Z`)
  return Number.isNaN(d.getTime()) ? datum : `${WEEKDAG_KORT[d.getUTCDay()]} ${nlDatum(datum)}`
}

/** Een moment zoals Amsterdam het ziet, voor op het scherm ("Mon 5 Oct, 20:30"). */
export function alsAmsterdam(iso: string | null | undefined): string {
  return alsZone(iso, AMSTERDAM)
}

/** Met de zone erbij: "Mon 5 Oct, 22:50 (Asia/Manila)". */
export function metZone(iso: string | null | undefined, zone: string = AMSTERDAM): string {
  const tekst = alsZone(iso, zone)
  return tekst === '—' ? tekst : `${tekst} (${zoneLabel(zone)})`
}

/** Voor een detailregel: bij een andere zone ook Amsterdam erbij
 *  ("Tue 20 Oct, 22:50 (Asia/Manila) = Tue 20 Oct, 16:50 Amsterdam"). */
export function metZoneEnAmsterdam(iso: string | null | undefined, zone: string = AMSTERDAM): string {
  const tekst = metZone(iso, zone)
  if (zone === AMSTERDAM || tekst === '—') return tekst
  return `${tekst} = ${alsZone(iso, AMSTERDAM)} Amsterdam`
}

/**
 * De bevestigingsregel onder de tijdkiezer: "22:50 Asia/Manila = 16:50
 * Amsterdam". Valt de dag in Amsterdam anders, dan staan de datums erbij
 * ("Wed 21 Oct, 01:00 Asia/Manila = Tue 20 Oct, 19:00 Amsterdam"). Null bij
 * Amsterdam zelf (dan zou er twee keer hetzelfde staan) of bij een ongeldige
 * datum/tijd.
 */
export function bevestigingsRegel(datum: string, tijd: string, zone: string): string | null {
  if (zone === AMSTERDAM) return null
  const iso = zoneNaarUtc(datum, tijd, zone)
  const ams = zoneDelen(iso, AMSTERDAM)
  if (!iso || !ams) return null
  if (ams.datum === datum) return `${tijd} ${zoneLabel(zone)} = ${ams.tijd} Amsterdam`
  return `${alsZone(iso, zone)} ${zoneLabel(zone)} = ${alsZone(iso, AMSTERDAM)} Amsterdam`
}
